import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Progress } from '../../components/ui/progress';
import { coverGradient } from '../../lib/cover';
import { cn } from '../../lib/utils';

export interface GapGridIssue {
  number: string;
  owned: boolean;
  imageUrl?: string;
  /** More than one cover variant of the same number. */
  variantCount?: number;
}

interface IssueGapGridProps {
  seriesTitle: string;
  issues: GapGridIssue[];
  /** Expected total; larger than issues.length for an ongoing series. */
  expectedCount?: number;
  onSelect?: (issue: GapGridIssue) => void;
  onWish?: (issue: GapGridIssue) => void;
}

/**
 * The gap view: every issue of a run as a tile, owned ones in colour, missing
 * ones as a dashed placeholder that can go straight onto the wishlist.
 */
export function IssueGapGrid({
  seriesTitle,
  issues,
  expectedCount,
  onSelect,
  onWish,
}: IssueGapGridProps) {
  const { t } = useTranslation();
  const total = expectedCount ?? issues.length;
  const owned = issues.filter((issue) => issue.owned).length;

  return (
    <div className="flex flex-col gap-4">
      <Progress value={owned} max={total} label={t('series.progress', { owned, total })} />

      <ul className="grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-2">
        {issues.map((issue) => (
          // Long runs (hundreds of issues): tiles off screen are not laid out
          // or painted until scrolled near.
          <li
            key={`${issue.number}-${issue.owned ? 'owned' : 'missing'}`}
            className="[contain-intrinsic-size:auto_7rem] [content-visibility:auto]"
          >
            <button
              type="button"
              onClick={() => (issue.owned ? onSelect?.(issue) : onWish?.(issue))}
              aria-label={`${seriesTitle} ${issue.number} – ${
                issue.owned ? t('series.owned') : t('series.missing')
              }`}
              className={cn(
                'group relative block aspect-[2/3] w-full overflow-hidden rounded-lg border text-left transition-transform',
                'hover:-translate-y-0.5 focus-visible:-translate-y-0.5',
                issue.owned
                  ? 'border-border bg-surface-raised'
                  : 'border-dashed border-border bg-surface/40 halftone',
              )}
              style={
                issue.owned && !issue.imageUrl
                  ? { backgroundImage: coverGradient(seriesTitle) }
                  : undefined
              }
            >
              {issue.imageUrl ? (
                <img
                  src={issue.imageUrl}
                  alt=""
                  loading="lazy"
                  className={cn(
                    'size-full object-cover',
                    // Missing volumes stay recognisable but clearly not owned.
                    !issue.owned &&
                      'opacity-35 grayscale transition-opacity group-hover:opacity-60',
                  )}
                />
              ) : null}

              <span
                className={cn(
                  'absolute inset-x-0 bottom-0 px-1 py-0.5 text-center font-display text-sm',
                  issue.owned || issue.imageUrl
                    ? 'bg-background/70 text-foreground backdrop-blur-sm'
                    : 'text-muted',
                )}
              >
                {issue.number}
              </span>

              {issue.variantCount && issue.variantCount > 1 ? (
                <span className="absolute top-1 right-1 rounded bg-accent px-1 text-[0.6rem] font-semibold text-accent-foreground">
                  {issue.variantCount}
                </span>
              ) : null}

              {!issue.owned ? (
                <span className="absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <span className="grid size-7 place-items-center rounded-full bg-accent text-accent-foreground">
                    <Plus className="size-4" />
                  </span>
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
